import { MAX_CIRCLE_SHAPES, MIN_CIRCLE_SHAPES } from "../../../const";
import type { CircleArc, CircleDirection, CircleState } from "../../../types";
import styles from "./InlinePanel.module.css";

interface AlignCirclePanelProps {
  readonly state: CircleState;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onRadius: (delta: number) => void;
  readonly onAngle: (deltaDeg: number) => void;
  readonly onSize: (delta: number) => void;
  readonly onDirection: (direction: CircleDirection) => void;
  readonly onArc: (arc: CircleArc) => void;
  readonly onAddShape: () => void;
  readonly onRemoveShape: () => void;
}

/**
 * Inline control panel for Align Circle — nudge the arrangement's radius,
 * angle and size, add/remove shapes from the ring, and pick the sweep
 * direction and arc after the initial layout. Slides down below the Arrange
 * section (the prototype's `at-circle-panel`).
 */
export function AlignCirclePanel({
  state,
  busy,
  onClose,
  onRadius,
  onAngle,
  onSize,
  onDirection,
  onArc,
  onAddShape,
  onRemoveShape,
}: AlignCirclePanelProps): React.ReactElement {
  const shapeCount = state.shapeIds.length;
  return (
    <fieldset className={styles.panel} aria-label="Arrange Circle options">
      <div className={styles.header}>
        <span className={styles.title}>Arrange Circle</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <div className={styles.stepper}>
        <span>Radius</span>
        <div className={styles.stepperControls}>
          <button type="button" aria-label="Decrease radius" disabled={busy} onClick={() => onRadius(-10)}>
            −
          </button>
          <span className={styles.stepperValue}>{Math.round(state.radius)}</span>
          <button type="button" aria-label="Increase radius" disabled={busy} onClick={() => onRadius(10)}>
            +
          </button>
        </div>
      </div>

      <div className={styles.stepper}>
        <span>Angle</span>
        <div className={styles.stepperControls}>
          <button type="button" aria-label="Decrease angle" disabled={busy} onClick={() => onAngle(-15)}>
            −
          </button>
          <span className={styles.stepperValue}>{Math.round(state.angleDeg)}°</span>
          <button type="button" aria-label="Increase angle" disabled={busy} onClick={() => onAngle(15)}>
            +
          </button>
        </div>
      </div>

      <div className={styles.stepper}>
        <span>Size</span>
        <div className={styles.stepperControls}>
          <button type="button" aria-label="Decrease size" disabled={busy} onClick={() => onSize(-10)}>
            −
          </button>
          <span className={styles.stepperValue}>{Math.round(state.size)}%</span>
          <button type="button" aria-label="Increase size" disabled={busy} onClick={() => onSize(10)}>
            +
          </button>
        </div>
      </div>

      <div className={styles.stepper}>
        <span>Shapes</span>
        <div className={styles.stepperControls}>
          <button
            type="button"
            aria-label="Remove shape"
            disabled={busy || shapeCount <= MIN_CIRCLE_SHAPES}
            onClick={onRemoveShape}
          >
            −
          </button>
          <span className={styles.stepperValue}>{shapeCount}</span>
          <button
            type="button"
            aria-label="Add shape"
            disabled={busy || shapeCount >= MAX_CIRCLE_SHAPES}
            onClick={onAddShape}
          >
            +
          </button>
        </div>
      </div>

      <div className={styles.stepper}>
        <span>Direction</span>
        <select
          className={styles.select}
          aria-label="Direction"
          disabled={busy}
          value={state.direction}
          onChange={(e) => onDirection(e.target.value as CircleDirection)}
        >
          <option value="cw">Clockwise</option>
          <option value="ccw">Counter-clockwise</option>
        </select>
      </div>

      <div className={styles.stepper}>
        <span>Arc</span>
        <select
          className={styles.select}
          aria-label="Arc"
          disabled={busy}
          value={state.arc}
          onChange={(e) => onArc(e.target.value as CircleArc)}
        >
          <option value="full">Full (360°)</option>
          <option value="semi">Semi (180°)</option>
          <option value="quarter">Quarter (90°)</option>
        </select>
      </div>
    </fieldset>
  );
}
